import Typography from "@mui/joy/Typography";
import classNames from "classnames";
import { useContext } from "react";
import { useForm } from "react-hook-form";
import { Permission, Role } from "../../../models/auth";
import { useSnackbar } from "../../../shared/components/layout/SnackbarContext";
import TitleStylizedUnderline from "../../../shared/components/ui/TitleStylizedUnderline";
import { IdentityProviderClientContext } from "../../../shared/services/api/contexts";
import { UserContext } from "../../admin/providers/UserProvider";

type FormValues = {
  email: string;
  firstName: string;
  lastName: string;
  organization: string;
  role: Role;
};

export function UserProfileAttributes() {
  const auth = useContext(IdentityProviderClientContext);
  const { user } = useContext(UserContext);
  const { showSnackbar } = useSnackbar();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isLoading },
  } = useForm<FormValues>({
    defaultValues: async () => {
      return {
        email: user?.email ?? "",
        firstName: user?.attributes.given_name ?? "",
        lastName: user?.attributes.name ?? "",
        organization: user?.attributes.organization ?? "",
        role: user?.attributes.role ?? Role.ADMIN,
      };
    },
  });
  const onSubmit = handleSubmit(async (data: FormValues) => {
    try {
      // For cloud users, always set the default cloud permissions
      const cloudPermissions = [
        Permission.PATIENT,
        Permission.DOCUMENT,
        Permission.COHORT,
        Permission.PROJECT,
        Permission.IDENTIFYING_DATA,
      ];

      await auth.setUserAttributes(data.email, {
        given_name: data.firstName,
        name: data.lastName,
        organization: data.organization,
        role: data.role,
        permissions: cloudPermissions,
      });
    } catch (e: any) {
      showSnackbar({ text: e.message, color: "danger" });
    }
  });
  return (
    <>
      <Typography level="h2">
        <TitleStylizedUnderline>Personal profile</TitleStylizedUnderline>
      </Typography>
      <hr />
      <form onSubmit={onSubmit} style={{ opacity: isLoading ? 0.5 : 1 }}>
        <div className="block">
          <div className="field-body">
            <div className="field">
              <label className="label" htmlFor="firstName">
                First name
              </label>
              <p className="control">
                <input
                  id="firstName"
                  className={errors.firstName ? "is-danger input" : "input"}
                  {...register("firstName", { required: "Field required." })}
                  placeholder="Enter first name"
                  disabled={isLoading || isSubmitting}
                />
              </p>
              <p className="help is-danger">{errors.firstName?.message}</p>
            </div>
            <div className="field">
              <label className="label" htmlFor="lastName">
                Last name
              </label>
              <p className="control">
                <input
                  id="lastName"
                  className={errors.lastName ? "is-danger input" : "input"}
                  {...register("lastName", { required: "Field required." })}
                  placeholder="Enter last name"
                  disabled={isLoading || isSubmitting}
                />
              </p>
              <p className="help is-danger">{errors.lastName?.message}</p>
            </div>
          </div>
          <div className="field">
            <label className="label" htmlFor="email">
              Email
            </label>
            <p className="control">
              <input
                id="email"
                className={errors.email ? "is-danger input" : "input"}
                {...register("email", { required: "Field required." })}
                placeholder="Enter email address"
                disabled={true}
              />
            </p>
            <p className="help is-danger">{errors.email?.message}</p>
          </div>
          <div className="field">
            <label className="label" htmlFor="organization">
              Organization
            </label>
            <p className="control">
              <input
                id="organization"
                className={errors.organization ? "is-danger input" : "input"}
                {...register("organization", { required: "Field required." })}
                placeholder="Enter organization"
                disabled={isLoading || isSubmitting}
              />
            </p>
            <p className="help">The company, organization, or institution you are working for.</p>
            <p className="help is-danger">{errors.organization?.message}</p>
          </div>
          <button
            className={classNames("button", isSubmitting && "is-loading")}
            type="submit"
            disabled={isLoading || isSubmitting}
          >
            Update profile
          </button>
        </div>
      </form>
    </>
  );
}
